import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    BiArrowBack, BiPhone, BiEnvelope, BiCopy, BiCheck, BiLinkExternal, BiDotsHorizontalRounded,
    BiMap, BiTimeFive, BiRefresh, BiErrorCircle, BiChevronDown, BiLogoWhatsapp, BiCar, BiInfoCircle,
} from "react-icons/bi";
import Handler from "../../../Helpers/Handler";
import { currencyFormatter, formatDateTime, hourBetweenDate, textCapitalize } from "../../../Helpers/Utils";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import usePaymentEvents from "../../../Helpers/usePaymentEvents";
import UploadStartImagesModal from "./UploadStartImagesModal";
import StartTripModal from "./StartTripModal";
import ExtendTripModal from "./ExtendTripModal";
import CloseTripModal from "./CloseTripModal";
import EditEndTimeModal from "./EditEndTimeModal";
import CompleteTripModal from "./CompleteTripModal";
import DamageModal from "./DamageModal";
import InspectModal from "./InspectModal";
import CancelModal from "./CancelModal";
import CarChangeModal from "./CarChangeModal";
import SwapCarModal from "./SwapCarModal";
import PaymentBreakup from "./PaymentBreakup";
import RefundModal from "./RefundModal";
import { listBackPath } from "../../../Helpers/useListParams";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

// Stage tone drives the pill colour everywhere on the page.
const STAGE_TONE = {
    Initiated: "neutral",
    Booked: "info",
    Started: "go",
    Ended: "info",
    Completed: "slate",
    Pending: "warn",
    Closed: "ok",
    Cancelled: "bad",
};

const FLOW = ["Initiated", "Booked", "Started", "Ended", "Completed", "Closed"];

// PricingUtils.cancellationRefund's line components are fixed camelCase keys.
const REFUND_LINE_LABEL = {
    rental: "Rental",
    securityDeposit: "Security deposit",
    deliveryFee: "Delivery fee",
    pickupFee: "Pickup fee",
    protectionFee: "Trip protection",
    platformFee: "Platform fee",
    convenienceFee: "Convenience fee",
};

const STATUS_NOTE = {
    Initiated: "Waiting for payment",
    Pending: "Settlement not yet resolved",
    Ended: "Car handed back. Complete the trip once it is picked up",
    Completed: "Waiting for the fine charges",
    Closed: "Trip closed and settled",
};

const PAID_STATES = ["captured", "partially_refunded", "refunded"];
const ACTIVITY_PREVIEW = 8;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDay = (value) => {
    const d = toDate(value);
    return d ? d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" }) : "-";
};
const fmtYear = (value) => {
    const d = toDate(value);
    return d ? d.getFullYear() : "";
};
const fmtTime = (value) => {
    const d = toDate(value);
    return d ? d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() : "-";
};

const formatDuration = (startDate, endDate) => {
    const result = hourBetweenDate(startDate, endDate);
    if (!result?.status) return "-";
    const days = Math.floor(result.hours / 24);
    const hrs = result.hours % 24;
    const parts = [];
    if (days) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hrs || !days) parts.push(`${hrs} hr${hrs !== 1 ? "s" : ""}`);
    return parts.join(" ");
};

const spanText = (ms) => {
    const mins = Math.max(0, Math.round(ms / 60000));
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    if (d) return `${d}d ${h}h`;
    if (h) return `${h}h ${m}m`;
    return `${m}m`;
};

const readDistance = (details) => details?.distanceKm ?? details?.distance ?? null;
const readFee = (details) => details?.fee ?? details?.net ?? details?.amount ?? null;
const readAddress = (details) => details?.address || details?.location || "";
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const sum = (rows, pick) => rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);
const money = (value) => currencyFormatter(value ?? 0);

const copyText = async (text, label) => {
    if (!text) return;
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    } catch {
        toast.error("Could not copy automatically. Select the text and copy it manually.");
    }
};

// "7:05" - minutes and seconds left, for the unpaid-booking countdown.
const clockText = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// Re-renders the page every minute so countdowns stay honest without a socket.
const useNow = (intervalMs = 60000) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
};

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

const Pill = ({ tone = "neutral", children, dot = true }) => (
    <span className={`bv-pill bv-pill--${tone}`}>
        {dot && <i aria-hidden="true" />}
        {children}
    </span>
);

const Panel = ({ title, aside, children, className = "", id }) => (
    <section className={`bv-panel ${className}`} aria-labelledby={id ? `${id}-title` : undefined} id={id}>
        {(title || aside) && (
            <div className="bv-panel-head">
                <h2 id={id ? `${id}-title` : undefined}>{title}</h2>
                {aside && <div className="bv-panel-aside">{aside}</div>}
            </div>
        )}
        {children}
    </section>
);

const KV = ({ label, children, mono }) => (
    <div className="bv-kv">
        <dt>{label}</dt>
        <dd className={mono ? "bv-mono" : ""}>{children ?? "-"}</dd>
    </div>
);

const CopyChip = ({ text, label }) => (
    <button type="button" className="bv-icon-btn" onClick={() => copyText(text, label)} aria-label={`Copy ${label}`} title={`Copy ${label}`}>
        <BiCopy aria-hidden="true" />
    </button>
);

// Button + popover. Closes on outside click and Escape; arrow keys are not
// needed for a short list, Tab order follows the DOM.
const Menu = ({ label, items }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!items.length) return null;
    return (
        <div className="bv-menu" ref={ref}>
            <button type="button" className="bv-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
                <BiDotsHorizontalRounded aria-hidden="true" /> {label}
            </button>
            {open && (
                <div className="bv-menu-pop" role="menu">
                    {items.map((item, i) => {
                        if (item.divider) return <div key={`d${i}`} className="bv-menu-sep" role="separator" />;
                        if (item.heading) return <div key={`h${i}`} className="bv-menu-heading">{item.heading}</div>;
                        const cls = `bv-menu-item${item.danger ? " is-danger" : ""}`;
                        if (item.href) {
                            return (
                                <a key={item.key} role="menuitem" className={cls} href={item.href} target={item.external ? "_blank" : undefined} rel="noopener noreferrer" onClick={() => setOpen(false)}>
                                    {item.icon}{item.label}
                                </a>
                            );
                        }
                        return (
                            <button
                                key={item.key}
                                type="button"
                                role="menuitem"
                                className={cls}
                                disabled={item.disabled}
                                title={item.disabled ? item.reason : undefined}
                                onClick={() => { setOpen(false); item.onClick && item.onClick(); }}
                            >
                                {item.icon}{item.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const Disclosure = ({ title, summary, defaultOpen = false, tone, children }) => (
    <details className={`bv-disc${tone ? ` bv-disc--${tone}` : ""}`} open={defaultOpen}>
        <summary>
            <span className="bv-disc-title">{title}</span>
            {summary && <span className="bv-disc-sum">{summary}</span>}
            <BiChevronDown className="bv-disc-caret" aria-hidden="true" />
        </summary>
        <div className="bv-disc-body">{children}</div>
    </details>
);

const Photos = ({ urls, label }) => (
    urls?.length ? (
        <div className="bv-photos" aria-label={label}>
            {urls.map((src, i) => (
                <a key={`${i}-${src}`} href={src} target="_blank" rel="noopener noreferrer" title={`${label} ${i + 1}`}>
                    <img src={src} alt={`${label} ${i + 1}`} loading="lazy" />
                </a>
            ))}
        </div>
    ) : null
);

const SkeletonPage = () => (
    <div className="bv"><div className="bv-page" aria-busy="true" aria-live="polite">
        <div className="bv-bar bv-bar--skeleton"><span className="bv-sk" style={{ width: 240, height: 22 }} /><span className="bv-sk" style={{ width: 150, height: 34 }} /></div>
        <div className="bv-band">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="bv-band-cell"><span className="bv-sk" style={{ width: 70, height: 10 }} /><span className="bv-sk" style={{ width: 120, height: 22, marginTop: 10 }} /></div>)}</div>
        <div className="bv-grid">
            <div className="bv-main">
                <div className="bv-panel"><span className="bv-sk" style={{ width: "100%", height: 190 }} /></div>
                <div className="bv-panel"><span className="bv-sk" style={{ width: "100%", height: 120 }} /></div>
                <div className="bv-panel"><span className="bv-sk" style={{ width: "100%", height: 260 }} /></div>
            </div>
            <div className="bv-side">
                <div className="bv-panel"><span className="bv-sk" style={{ width: "100%", height: 300 }} /></div>
                <div className="bv-panel"><span className="bv-sk" style={{ width: "100%", height: 220 }} /></div>
            </div>
        </div>
        <span className="bv-sr">Loading booking</span>
    </div></div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const BookingView = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const { hasPermission: can } = useAuth();
    const [booking, setBooking] = useState(null);
    // Every second while the booking is unpaid (the expiry countdown), every minute otherwise.
    const now = useNow(booking?.bookingStage === "Initiated" ? 1000 : 60000);
    const [logs, setLogs] = useState([]);
    const [payments, setPayments] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showUploadImages, setShowUploadImages] = useState(false);
    const [showStartTrip, setShowStartTrip] = useState(false);
    const [showExtend, setShowExtend] = useState(false);
    const [showCloseTrip, setShowCloseTrip] = useState(false);
    const [showEndTime, setShowEndTime] = useState(false);
    const [showCompleteTrip, setShowCompleteTrip] = useState(false);
    const [showDamage, setShowDamage] = useState(false);
    const [showInspect, setShowInspect] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    const [showCarChange, setShowCarChange] = useState(false);
    const [showSwap, setShowSwap] = useState(false);
    const [linkBusy, setLinkBusy] = useState(false);
    const [confirmKey, setConfirmKey] = useState(null);
    const [showAllAttention, setShowAllAttention] = useState(false);
    const [activityFilter, setActivityFilter] = useState("all");
    const [activityAll, setActivityAll] = useState(false);

    // load({ quiet: true }) refetches in place (no skeleton) - used when a
    // live payment event arrives. The argument may also be a click event
    // (modal callbacks pass load straight through), so only read `quiet`.
    const load = useCallback(async (options) => {
        const quiet = !!options?.quiet;
        if (!quiet) setLoading(true);
        const [detailResponse, logResponse, paymentResponse] = await Promise.all([
            Handler({ method: "get", url: `/staff/booking/${bookingId}` }),
            can("booking.view.log") ? Handler({ method: "get", url: `/staff/booking/${bookingId}/log`, params: { limit: 40 } }) : Promise.resolve(null),
            can("booking.view.payments") ? Handler({ method: "get", url: `/staff/booking/${bookingId}/payment` }) : Promise.resolve(null),
        ]);
        if (detailResponse?.success && detailResponse?.data?.data) {
            setBooking(detailResponse.data.data);
            setError(null);
        } else if (!quiet) {
            setBooking(null);
            setError(detailResponse?.data?.message || "Booking not found");
        }
        setLogs(logResponse?.success ? (logResponse?.data?.data || []) : []);
        setPayments(paymentResponse?.success ? (paymentResponse?.data?.data?.payments || []) : []);
        setRefunds(paymentResponse?.success ? (paymentResponse?.data?.data?.refunds || []) : []);
        if (!quiet) setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingId]);

    useEffect(() => { load(); }, [load]);

    // Live payment status for this booking. Events are only a nudge: a
    // payment, its link and the booking stage each emit separately, so we
    // debounce and refetch once over REST (never trust partial payloads).
    const [liveNote, setLiveNote] = useState(null);
    const liveTimer = useRef(null);
    usePaymentEvents({
        bookingId,
        onEvent: (event) => {
            if ((event.kind === "payment" && event.status === "captured") || (event.kind === "link" && event.status === "paid")) {
                setLiveNote({ tone: "ok", text: `Payment received${event.amount ? `: ${currencyFormatter(event.amount)}` : ""}` });
                toast.success("Payment received");
            } else if ((event.kind === "payment" || event.kind === "link") && event.status === "failed") {
                setLiveNote({ tone: "bad", text: "A payment attempt failed" });
            } else if (event.kind === "link" && (event.status === "expired" || event.status === "cancelled")) {
                setLiveNote({ tone: "neutral", text: `Payment link ${event.status}` });
            } else if (event.kind === "refund") {
                setLiveNote({ tone: "info", text: `Refund ${event.status || "updated"}` });
            }
            if (liveTimer.current) clearTimeout(liveTimer.current);
            liveTimer.current = setTimeout(() => load({ quiet: true }), 500);
        },
        onResync: () => load({ quiet: true }),
    });
    useEffect(() => () => liveTimer.current && clearTimeout(liveTimer.current), []);

    const resendPaymentLink = async (linkId, medium) => {
        if (!linkId) return;
        setLinkBusy(true);
        const response = await Handler({ method: "post", url: `/staff/flow/payment-link/${linkId}/resend`, data: { medium } });
        setLinkBusy(false);
        if (response?.success) {
            toast.success(`Payment link resent by ${medium}`);
        } else {
            toast.error(response?.data?.message || "Could not resend this payment link");
        }
    };

    const expirePaymentLink = async (linkId) => {
        if (!linkId) return;
        setLinkBusy(true);
        const response = await Handler({ method: "post", url: `/staff/flow/payment-link/${linkId}/expire` });
        setLinkBusy(false);
        setConfirmKey(null);
        if (response?.success) {
            toast.success("Payment link expired");
            load({ quiet: true });
        } else {
            toast.error(response?.data?.message || "Could not expire this payment link");
        }
    };

    // One link per unpaid order: Booking, Extension or Settlement.
    const createPaymentLink = async (orderId) => {
        setLinkBusy(true);
        const response = await Handler({ method: "post", url: `/staff/flow/${bookingId}/payment-link`, data: orderId ? { orderId } : {} });
        setLinkBusy(false);
        if (response?.success) {
            toast.success("Payment link ready");
            load({ quiet: true });
        } else {
            toast.error(response?.data?.message || "Could not create a payment link");
        }
    };

    // Cancels an extension that was never paid (and expires its link).
    const cancelExtension = async (orderId) => {
        setLinkBusy(true);
        const response = await Handler({ method: "post", url: `/staff/flow/${bookingId}/extension/${orderId}/cancel` });
        setLinkBusy(false);
        setConfirmKey(null);
        if (response?.success) {
            toast.success("Extension cancelled");
            load({ quiet: true });
        } else {
            toast.error(response?.data?.message || "Could not cancel this extension");
        }
    };

    /* ---- derived state (memoised on the raw data, safe before early returns) ---- */

    // A flagged payment (paid twice / paid after it was no longer needed) is not part of the
    // booking's money: it is refunded through its own approval, so it stays out of every total.
    const appliedPayments = useMemo(() => payments.filter((p) => !p.duplicate?.reason), [payments]);
    const paidRows = useMemo(() => appliedPayments.filter((p) => PAID_STATES.includes(p.status)), [appliedPayments]);
    const paidTotal = useMemo(() => sum(paidRows, (p) => p.totalAmount), [paidRows]);
    const refundedTotal = useMemo(() => sum(appliedPayments, (p) => p.refundedAmount), [appliedPayments]);

    const activity = useMemo(() => {
        if (!booking) return [];
        const items = [];
        if (booking.createdAt) items.push({ id: "created", kind: "status", at: booking.createdAt, title: "Booking created", sub: booking.preBookDetails?.orderId ? `Order ${booking.preBookDetails.orderId}` : "" });
        (booking.stageHistory || []).forEach((s, i) => items.push({ id: `stage${s.id || i}`, kind: "status", at: s.at, title: `Status changed to ${s.stage}`, tone: STAGE_TONE[s.stage] }));
        payments.forEach((p) => items.push({
            id: `pay${p.orderId}`,
            kind: "payment",
            at: p.updatedAt || p.createdAt,
            title: `${p.paymentFor} payment ${String(p.status || "").replace(/_/g, " ")}`,
            sub: `${money(p.totalAmount)} · ${p.orderId}`,
            tone: PAID_STATES.includes(p.status) ? "ok" : p.status === "failed" ? "bad" : "neutral",
        }));
        refunds.forEach((r, i) => items.push({ id: `ref${r.refundId || i}`, kind: "payment", at: r.createdAt, title: "Refund issued", sub: `${money(r.amount)} · ${r.refundId || r.paymentId}`, tone: "info" }));
        logs.forEach((log, i) => items.push({
            id: `log${i}`,
            kind: "ops",
            at: log.createdAt,
            title: log.action,
            sub: log.msg || log.previousData?.msg,
            who: log.userType === "Admin" || log.userType === "Staff" ? `${log.userType} ${log.userId || ""}`.trim() : "Customer",
            priority: !!log.priority,
            changes: log.previousData?.data,
        }));
        return items.filter((i) => toDate(i.at)).sort((a, b) => toDate(b.at) - toDate(a.at));
    }, [booking, payments, refunds, logs]);

    /* ---- loading / error ---- */

    if (loading) return <SkeletonPage />;

    if (!booking) {
        return (
            <div className="bv"><div className="bv-page">
                <div className="bv-error" role="alert">
                    <BiErrorCircle aria-hidden="true" />
                    <div>
                        <strong>This booking could not be loaded</strong>
                        <p>{error || "Something went wrong."} Check the booking ID, or try again.</p>
                        <div className="bv-error-actions">
                            <button type="button" className="bv-btn bv-btn--primary" onClick={() => load()}><BiRefresh aria-hidden="true" /> Try again</button>
                            <button type="button" className="bv-btn" onClick={() => navigate(listBackPath("/booking"))}><BiArrowBack aria-hidden="true" /> Back to bookings</button>
                        </div>
                    </div>
                </div>
            </div></div>
        );
    }

    const { car, customer, flags, preBookDetails, tripDetails, fineDetails, settlement, damage, cancelDetails, deliveryTask, pickupTask, prevExtendOrder, prevPaymentLink, stageHistory } = booking;
    const stage = booking.bookingStage;
    const bookingTotal = booking.totalAmount ?? 0;

    const prevSettlementOrder = payments.find((p) => p.paymentFor === "Settlement" && p.status === "created");
    const deliveryDistance = readDistance(preBookDetails?.deliveryDetails);
    const pickupDistance = readDistance(preBookDetails?.pickupDetails);
    const deliveryFee = readFee(preBookDetails?.deliveryDetails);
    const pickupFee = readFee(preBookDetails?.pickupDetails);
    const hasTripExecution = !!(tripDetails?.startTime || tripDetails?.endTime);

    /* ---- trip execution: used and excess, worked out by the server at trip close ---- */
    const tripClosed = !!tripDetails?.endTime;
    const tripExcess = tripDetails?.excess || null;
    // The policy saved on the booking (Settings > Booking Policy when it was made).
    const bookingPolicy = preBookDetails?.policy || {};
    const endPhotoLimits = { min: bookingPolicy.photos?.end?.min, max: bookingPolicy.photos?.end?.max };
    const startPhotoLimits = { min: bookingPolicy.photos?.start?.min, max: bookingPolicy.photos?.start?.max };
    const coverAmount = Number(preBookDetails?.tripProtection?.coverAmount || 0);
    const durationText = (hours) => {
        const total = Math.max(Math.round(Number(hours) * 60), 0);
        const h = Math.floor(total / 60);
        const m = total % 60;
        return h ? (m ? `${h} h ${m} m` : `${h} h`) : `${m} m`;
    };
    const usedTime = tripClosed && tripDetails?.startTime ? durationText((new Date(tripDetails.endTime) - new Date(tripDetails.startTime)) / 3600000) : null;
    const usedKmValue = tripClosed && tripDetails?.endKm != null && tripDetails?.startKm != null ? tripDetails.endKm - tripDetails.startKm : null;
    const usedFastag = tripClosed && tripDetails?.endFastTag != null && tripDetails?.startFastTag != null ? Math.max(Math.round((tripDetails.startFastTag - tripDetails.endFastTag) * 100) / 100, 0) : null;
    const fuelUsedText = tripClosed && tripDetails?.endFuel?.length
        ? (tripDetails.startFuel || []).map((f) => {
            const end = tripDetails.endFuel.find((e) => e.fuelType === f.fuelType);
            return `${f.fuelType} ${end ? Math.max(f.percentage - end.percentage, 0) : 0}%`;
        }).join(", ")
        : null;
    const excessCell = (value, text) => (tripExcess && Number(value) > 0 ? <span className="bv-excess">{text}</span> : <span className="bv-excess-none">{tripClosed && tripExcess ? "None" : "-"}</span>);
    const hasFineOrSettlement = !!(fineDetails && (fineDetails.excessKm || fineDetails.excessHour || fineDetails.excessFastag || fineDetails.fines?.length || fineDetails.excessFuel?.length)) || !!settlement;
    const hasCancellation = !!cancelDetails || !!booking.refundId;
    const hasStartImages = !!tripDetails?.startImage?.length;
    const startDate = preBookDetails?.startDate;
    const endDate = preBookDetails?.endDate;

    /* ---- payment state ---- */

    const bookingPaid = sum(paidRows.filter((p) => p.paymentFor === "Booking"), (p) => p.totalAmount);
    // Extensions and settlements are charged on top of the booking total, so
    // "total charged" grows as they are created (open) and captured (paid);
    // otherwise paid could exceed total, or the meter read 100% with money due.
    const extraPaid = stage === "Initiated" ? 0 : sum(paidRows.filter((p) => p.paymentFor !== "Booking"), (p) => p.totalAmount);
    // Open orders that still need money: everything unpaid except a stale Booking order once booked.
    const openOrders = payments.filter((p) => p.status === "created" && (stage === "Initiated" ? p.paymentFor === "Booking" : p.paymentFor !== "Booking"));
    const openExtra = stage === "Initiated" || stage === "Cancelled" ? 0 : sum(openOrders, (p) => p.totalAmount);
    const addOns = extraPaid + openExtra;
    const total = bookingTotal + addOns;
    const pendingAmount = stage === "Cancelled" ? 0 : stage === "Initiated" ? Math.max(bookingTotal - bookingPaid, 0) : openExtra;
    const liveLinks = booking.paymentLinks || (prevPaymentLink ? [{ ...prevPaymentLink, paymentFor: "Booking" }] : []);
    const linkFor = (kind, orderId) => liveLinks.find((l) => orderId && l.sourceOrderId === orderId) || (kind === "Booking" ? liveLinks.find((l) => l.paymentFor === "Booking") : null) || null;
    const bookingOrder = payments.find((p) => p.paymentFor === "Booking" && p.status === "created");
    const dueItems = stage === "Cancelled" ? [] : stage === "Initiated"
        ? (pendingAmount > 0 ? [{ key: "booking", kind: "Booking", title: "Booking payment", orderId: bookingOrder?.orderId, amount: bookingOrder?.totalAmount ?? pendingAmount, createdAt: bookingOrder?.createdAt }] : [])
        : openOrders.map((p) => ({
            key: p.orderId,
            kind: p.paymentFor,
            title: p.paymentFor === "Extension" ? `Extension${p.extraHours ? `, +${p.extraHours} hr${Number(p.extraHours) === 1 ? "" : "s"}` : ""}` : p.paymentFor === "Settlement" ? "Pending settlement" : p.paymentFor,
            orderId: p.orderId,
            amount: p.totalAmount,
            createdAt: p.createdAt,
        }));
    const failedAny = payments.some((p) => p.status === "failed") && !paidRows.length;

    let payLabel = "Not requested";
    let payTone = "neutral";
    if (refundedTotal > 0 && refundedTotal >= paidTotal && paidTotal > 0) { payLabel = "Refunded"; payTone = "info"; }
    else if (refundedTotal > 0) { payLabel = "Partly refunded"; payTone = "info"; }
    else if (bookingPaid > 0 && pendingAmount > 0) { payLabel = "Paid, balance due"; payTone = "warn"; }
    else if (bookingPaid > 0) { payLabel = "Paid"; payTone = "ok"; }
    else if (prevPaymentLink) { payLabel = "Awaiting payment"; payTone = "warn"; }
    else if (failedAny) { payLabel = "Payment failed"; payTone = "bad"; }
    else if (stage === "Cancelled") { payLabel = "No payment"; payTone = "neutral"; }
    const payPct = total > 0 ? Math.min(100, Math.round(((stage === "Initiated" ? bookingPaid : Math.min(paidTotal, total)) / total) * 100)) : 0;

    /* ---- timing ---- */

    const startMs = toDate(startDate)?.getTime();
    const endMs = toDate(endDate)?.getTime();
    // When an unpaid booking is cancelled automatically (Settings > Booking Policy > Payment expiry).
    const expiresMs = toDate(booking.preBookDetails?.expiresAt)?.getTime();
    let timing = null;
    if (stage === "Booked" && startMs) timing = startMs > now ? { text: `Starts in ${spanText(startMs - now)}`, tone: "info" } : { text: `Pickup was ${spanText(now - startMs)} ago`, tone: "warn" };
    else if (stage === "Started" && endMs) timing = endMs > now ? { text: `Due back in ${spanText(endMs - now)}`, tone: "go" } : { text: `Overdue by ${spanText(now - endMs)}`, tone: "bad" };
    else if (stage === "Initiated" && expiresMs) timing = expiresMs > now
        ? { text: `Unpaid. Cancels automatically in ${clockText(expiresMs - now)}`, tone: expiresMs - now < 2 * 60 * 1000 ? "bad" : "warn" }
        : { text: "Payment time is over. The booking is being cancelled", tone: "bad" };
    else if (stage === "Initiated" && startMs) timing = { text: startMs > now ? `Trip starts in ${spanText(startMs - now)}` : "Trip start time has passed", tone: "neutral" };
    const isOverdue = stage === "Started" && !tripDetails?.endTime && endMs && now > endMs;

    /* ---- attention ---- */

    const isFinished = ["Closed", "Cancelled"].includes(stage);
    // Sections this role may not see (set by the API from booking.view.*).
    const hid = (key) => (booking.hiddenSections || []).includes(key);
    const NO_ACCESS = "No access";
    const attention = [
        isOverdue && { text: `Trip is overdue. Expected back ${formatDateTime(endDate)}`, tone: "bad" },
        stage === "Initiated" && prevPaymentLink && !hid("payments") && { text: "Payment link sent, waiting for the customer to pay", tone: "warn" },
        stage === "Pending" && !hid("fines") && { text: "Settlement pending. Fine or deposit not yet resolved", tone: "warn" },
        stage === "Ended" && can("booking.trip.close") && { text: flags?.pickupPending ? "Trip ended. Complete it with the odometer and fuel once the car is picked up" : "Trip ended. Complete it with the odometer and fuel", tone: "warn" },
        stage === "Ended" && Number(tripExcess?.hours) > 0 && { text: `Returned ${tripExcess.lateMinutes} min late: ${tripExcess.hours} late hour${tripExcess.hours === 1 ? "" : "s"} will be charged`, tone: "neutral" },
        stage === "Completed" && { text: "Trip completed. Enter the fine charges to close the booking", tone: "warn" },
        flags?.damage && !hid("damage") && { text: "Damage logged. Review before closing out", tone: "bad" },
        !isFinished && !hid("handover") && flags?.deliveryPending && { text: "Delivery not yet completed", tone: "warn" },
        !isFinished && !hid("handover") && flags?.pickupPending && { text: "Pickup not yet completed", tone: "warn" },
        !isFinished && !hid("trip") && flags?.uploadImage && { text: "Start photos are still missing", tone: "neutral" },
        stage === "Booked" && tripDetails?.earlyStart && { text: `Early start allowed by admin: ${tripDetails.earlyStart.reason}`, tone: "neutral" },
        !isFinished && !hid("customer") && flags?.kyc === false && { text: "Customer KYC is not verified", tone: "neutral" },
        !isFinished && !hid("customer") && customer?.status === false && { text: "Customer account is deactivated", tone: "bad" },
        booking.paymentMode === "test" && { text: "Test booking: paid with the Razorpay test account, no real money moves. Left out of the dashboards and money totals.", tone: "warn" },
    ].filter(Boolean);

    /* ---- actions ---- */

    const startBlockers = [
        booking.startBlock?.message,
        flags?.kyc === false && "KYC is not verified",
        flags?.deliveryPending && "Delivery is not completed",
        customer?.status === false && "Customer is deactivated",
    ].filter(Boolean);
    // The start window comes from the booking's policy (or an admin's early start); flags.startWindow says when it opens.
    const startWindow = flags?.startWindow || null;
    const windowText = startWindow && !startWindow.open
        ? `Opens ${startWindow.opensAt ? formatDateTime(startWindow.opensAt) : ""}, ${startWindow.minutes ?? 30} minutes before pickup`
        : null;
    const startReason = startBlockers.length ? `Not ready: ${startBlockers.join(", ")}` : windowText || "Not ready yet";

    const acts = [];
    if (stage === "Booked" && (hasStartImages ? can("booking.trip.start") : can("booking.trip.startImages"))) {
        acts.push(hasStartImages
            ? { key: "start", label: "Start trip", tone: "go", primary: true, onClick: () => setShowStartTrip(true), disabled: !flags?.canStartTrip, reason: startReason }
            : { key: "upload", label: "Upload start photos", tone: "primary", primary: true, onClick: () => setShowUploadImages(true), disabled: !flags?.canStartTrip, reason: startReason });
    }
    // Ending a trip (Booking Flow v2): End trip (end photos; Started -> Ended, late hours fixed) ->
    // Complete trip (odometer, fuel; needs the pick-up) -> Fine charges (Calculate, then Submit).
    // Damage can be logged from Ended until the fine charges are submitted.
    if (stage === "Started") {
        if (can("booking.trip.close")) acts.push({ key: "close", label: "End trip", tone: "warn", primary: true, onClick: () => setShowCloseTrip(true) });
        if (can("booking.extend")) acts.push({ key: "extend", label: prevExtendOrder ? "Complete extension payment" : "Extend trip", onClick: () => setShowExtend(true) });
    }
    if (stage === "Ended" && can("booking.trip.close")) {
        acts.push({ key: "complete", label: "Complete trip", tone: "go", primary: true, onClick: () => setShowCompleteTrip(true), disabled: !!flags?.pickupPending, reason: "Not ready: Pickup is not completed" });
        acts.push({ key: "close", label: "Change end photos", onClick: () => setShowCloseTrip(true) });
    }
    if (stage === "Completed" && can("booking.inspect")) acts.push({ key: "inspect", label: "Fine charges", tone: "primary", primary: true, onClick: () => setShowInspect(true) });
    if (stage === "Pending" && prevSettlementOrder && (can("booking.settle") || can("booking.inspect"))) acts.push({ key: "settle", label: "Collect balance", tone: "warn", primary: true, onClick: () => setShowInspect(true) });
    if (stage === "Initiated" && prevPaymentLink?.shortUrl && can("booking.paymentLink")) acts.push({ key: "copylink", label: "Copy payment link", tone: "primary", primary: true, onClick: () => copyText(prevPaymentLink.shortUrl, "Payment link") });
    // A booked trip changes its car with Change car; a trip in progress swaps it (both cars' photos and readings).
    if (stage === "Booked" && can("booking.carChange")) acts.push({ key: "car", label: "Change car", onClick: () => setShowCarChange(true) });
    if (stage === "Started" && can("booking.carChange")) acts.push({ key: "swap", label: "Swap car", onClick: () => setShowSwap(true), disabled: flags?.canSwapCar === false, reason: flags?.swapBlockedReason });
    // Test customers only (the server checks too): move the recorded end time of an Ended trip.
    if (stage === "Ended" && customer?.testMode === true) acts.push({ key: "endtime", label: "Edit end time (test)", onClick: () => setShowEndTime(true) });
    if (["Ended", "Completed"].includes(stage) && can("booking.damage.record")) acts.push({ key: "damage", label: flags?.damage ? "Edit damage" : "Add damage", onClick: () => setShowDamage(true) });

    const primaryAct = acts.find((a) => a.primary);
    const secondaryActs = acts.filter((a) => !a.primary);
    const inlineSecondary = secondaryActs.slice(0, 1);
    const menuActs = secondaryActs.slice(1);

    const phone = customer?.phone;
    const waPhone = (phone || "").replace(/\D/g, "");
    const menuItems = [];
    menuActs.forEach((a) => menuItems.push({ key: a.key, label: a.label, onClick: a.onClick, disabled: a.disabled, reason: a.reason }));
    if (menuItems.length) menuItems.push({ divider: true });
    if (phone || customer?.email) {
        menuItems.push({ heading: "Contact customer" });
        if (phone) menuItems.push({ key: "call", label: `Call ${phone}`, href: `tel:${phone}`, icon: <BiPhone aria-hidden="true" /> });
        if (waPhone) menuItems.push({ key: "wa", label: "WhatsApp", href: `https://wa.me/${waPhone}`, external: true, icon: <BiLogoWhatsapp aria-hidden="true" /> });
        if (customer?.email) menuItems.push({ key: "mail", label: "Email", href: `mailto:${customer.email}`, icon: <BiEnvelope aria-hidden="true" /> });
    }
    if (stage === "Initiated" && prevPaymentLink && can("booking.paymentLink")) {
        menuItems.push({ divider: true });
        menuItems.push({ heading: "Payment link" });
        menuItems.push({ key: "rs-email", label: "Resend by email", onClick: () => resendPaymentLink(prevPaymentLink.linkId, "email"), disabled: linkBusy });
        menuItems.push({ key: "rs-sms", label: "Resend by SMS", onClick: () => resendPaymentLink(prevPaymentLink.linkId, "sms"), disabled: linkBusy });
    }
    // Payments that still have money that can go back to the customer.
    const refundableRows = paidRows.filter((p) => p.paymentId && Number(p.totalAmount) - (Number(p.refundedAmount) || 0) > 0.009);
    if (refundableRows.length && can("booking.refund")) {
        menuItems.push({ divider: true });
        menuItems.push({ key: "refund", label: "Refund customer", onClick: () => setShowRefund(true) });
    }
    const dangerItems = [];
    if (["Initiated", "Booked"].includes(stage) && can("booking.cancel")) dangerItems.push({ key: "cancel", label: "Cancel booking", danger: true, onClick: () => setShowCancel(true) });
    if (stage === "Initiated" && prevPaymentLink && can("booking.paymentLink")) dangerItems.push({ key: "expire", label: "Expire payment link", danger: true, onClick: () => setConfirmKey(`expire:${prevPaymentLink.linkId}`) });
    if (dangerItems.length) { menuItems.push({ divider: true }); dangerItems.forEach((d) => menuItems.push(d)); }

    /* ---- journey ---- */

    const reached = {};
    (stageHistory || []).forEach((s) => { reached[s.stage] = s.at; });
    if (booking.createdAt && !reached.Initiated) reached.Initiated = booking.createdAt;
    let flow = FLOW;
    if (stage === "Pending") flow = ["Initiated", "Booked", "Started", "Ended", "Completed", "Pending", "Closed"];
    if (stage === "Cancelled") flow = [...FLOW.filter((s) => reached[s]), "Cancelled"];
    const stageIndex = flow.indexOf(stage);

    /* ---- ledger ---- */

    const ledger = [
        { label: "Rental", value: preBookDetails?.rentalFee ?? 0 },
        { label: `Trip protection${preBookDetails?.tripProtection?.name ? `, ${preBookDetails.tripProtection.name}` : ""}${preBookDetails?.tripProtection?.feePercentage != null ? ` (${preBookDetails.tripProtection.feePercentage}%)` : ""}`, value: preBookDetails?.tripProtection?.feeCharged ?? 0 },
        preBookDetails?.deliveryDetails && { label: `Delivery fee${deliveryDistance != null ? ` (${deliveryDistance} km)` : ""}`, value: deliveryFee ?? 0 },
        preBookDetails?.pickupDetails && { label: `Pickup fee${pickupDistance != null ? ` (${pickupDistance} km)` : ""}`, value: pickupFee ?? 0 },
        { label: "Convenience fee", value: preBookDetails?.convenienceFee ?? 0 },
        { label: "Platform fee", value: preBookDetails?.platformFee ?? 0 },
        !!preBookDetails?.coupon?.totalDiscount && { label: `Coupon${preBookDetails?.coupon?.couponCode ? ` ${preBookDetails.coupon.couponCode}` : ""}`, value: -preBookDetails.coupon.totalDiscount, credit: true },
        !!preBookDetails?.coins?.spent && { label: "Coins applied", value: -preBookDetails.coins.spent, credit: true },
    ].filter(Boolean);

    /* ---- activity ---- */

    const filteredActivity = activity.filter((a) => activityFilter === "all" || a.kind === activityFilter);
    const shownActivity = activityAll ? filteredActivity : filteredActivity.slice(0, ACTIVITY_PREVIEW);
    const carName = car ? [car.brand, car.modelName].filter(Boolean).join(" ") || car.registrationNo : null;
    const visibleAttention = showAllAttention ? attention : attention.slice(0, 1);

    return (
        <>
            <div className="bv">
                <div className="bv-page">

                    {/* 1. Command bar: identity, state and the actions that make sense now */}
                    <div className="bv-bar" role="toolbar" aria-label="Booking actions">
                        <div className="bv-bar-id">
                            <button type="button" className="bv-icon-btn bv-back" onClick={() => navigate(listBackPath("/booking"))} aria-label="Back to bookings"><BiArrowBack aria-hidden="true" /></button>
                            <div className="bv-bar-text">
                                <div className="bv-bar-title">
                                    <h1 className="bv-mono">{booking.bookingId}</h1>
                                    <Pill tone={STAGE_TONE[stage] || "neutral"}>{stage}</Pill>
                                    {/* Test mode: paid with the Razorpay test account (no real money); left out of the dashboards and totals. */}
                                    {booking.paymentMode === "test" ? <Pill tone="warn" dot={false}>TEST</Pill> : null}
                                    {!hid("payments") && <Pill tone={payTone}>{payLabel}</Pill>}
                                </div>
                                <div className="bv-bar-sub">
                                    Created {formatDateTime(booking.createdAt)}
                                    {preBookDetails?.orderId ? <> · Order <span className="bv-mono">{preBookDetails.orderId}</span></> : null}
                                </div>
                            </div>
                        </div>
                        <div className="bv-bar-right">
                            <div className="bv-bar-actions">
                                {inlineSecondary.map((a) => (
                                    <button key={a.key} type="button" className="bv-btn" onClick={a.onClick} disabled={a.disabled} title={a.disabled ? a.reason : undefined}>{a.label}</button>
                                ))}
                                <Menu label="More" items={menuItems} />
                                {primaryAct && (
                                    <button
                                        type="button"
                                        className={`bv-btn bv-btn--${primaryAct.tone || "primary"}`}
                                        onClick={primaryAct.onClick}
                                        disabled={primaryAct.disabled}
                                        aria-describedby={primaryAct.disabled ? "bv-primary-reason" : undefined}
                                    >
                                        {primaryAct.label}
                                    </button>
                                )}
                            </div>
                            {primaryAct?.disabled && <div id="bv-primary-reason" className="bv-bar-reason"><BiInfoCircle aria-hidden="true" /> {primaryAct.reason}</div>}
                        </div>
                    </div>

                    <section className={`bv-hero bv-hero--${STAGE_TONE[stage] || "neutral"}`} aria-label="Booking summary">
                        {/* 2. Attention: the one thing that needs a human, then the rest on demand */}
                        {(attention.length > 0 || liveNote) && (
                            <div className="bv-attn" role="status">
                                {liveNote && (
                                    <div className={`bv-attn-row bv-attn-row--${liveNote.tone}`}>
                                        <BiCheck aria-hidden="true" /><span>{liveNote.text}</span>
                                        <button type="button" className="bv-link" onClick={() => setLiveNote(null)}>Dismiss</button>
                                    </div>
                                )}
                                {visibleAttention.map((item) => (
                                    <div key={item.text} className={`bv-attn-row bv-attn-row--${item.tone}`}>
                                        <BiErrorCircle aria-hidden="true" /><span>{item.text}</span>
                                        {item === visibleAttention[0] && attention.length > 1 && (
                                            <button type="button" className="bv-link" aria-expanded={showAllAttention} onClick={() => setShowAllAttention((v) => !v)}>
                                                {showAllAttention ? "Show less" : `+${attention.length - 1} more`}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 3. Summary band: the answers to "when, how long, how much" in one glance */}
                        <div className="bv-band" role="group" aria-label="Trip summary">
                            <div className="bv-band-cell">
                                <div className="bv-eyebrow">Pickup</div>
                                <div className="bv-band-main">{fmtDay(startDate)}</div>
                                <div className="bv-band-sub">{fmtTime(startDate)} · {fmtYear(startDate)}</div>
                            </div>
                            <div className="bv-band-cell">
                                <div className="bv-eyebrow">Drop-off</div>
                                <div className="bv-band-main">{fmtDay(endDate)}</div>
                                <div className="bv-band-sub">{fmtTime(endDate)} · {fmtYear(endDate)}</div>
                            </div>
                            <div className="bv-band-cell">
                                <div className="bv-eyebrow">Duration</div>
                                <div className="bv-band-main">{formatDuration(startDate, endDate)}</div>
                                <div className="bv-band-sub">{preBookDetails?.package?.package ? `${textCapitalize(preBookDetails.package.package)} package` : "No package recorded"}</div>
                            </div>
                            <div className="bv-band-cell">
                                <div className="bv-eyebrow">Total</div>
                                <div className="bv-band-main bv-num">{hid("pricing") ? NO_ACCESS : money(total)}</div>
                                <div className="bv-band-sub">
                                    {hid("pricing") || hid("payments") ? "You don't have access" : pendingAmount > 0 ? <span className="bv-text-warn">{money(pendingAmount)} pending</span> : paidTotal > 0 ? <span className="bv-text-ok">{money(paidTotal)} paid</span> : "Nothing paid yet"}
                                </div>
                            </div>
                            <div className="bv-band-cell">
                                <div className="bv-eyebrow">Status</div>
                                <div className={`bv-band-main bv-text-${timing?.tone || "ink"}`}>{timing ? timing.text : stage}</div>
                                <div className="bv-band-sub">{stage === "Cancelled" && cancelDetails?.at ? `Cancelled ${formatDateTime(cancelDetails.at)}` : reached[stage] ? `${stage} ${fmtDay(reached[stage])}, ${fmtTime(reached[stage])}` : (STATUS_NOTE[stage] || stage)}</div>
                            </div>
                        </div>

                        {/* 4. Journey rail */}
                        <ol className="bv-journey" aria-label="Booking progress">
                            {flow.map((s, i) => {
                                const state = s === "Cancelled" ? "bad" : i < stageIndex || (i === stageIndex && s === "Closed") ? "done" : i === stageIndex ? "now" : "todo";
                                return (
                                    <li key={s} className={`is-${state}`} aria-current={i === stageIndex ? "step" : undefined}>
                                        <span className="bv-journey-dot" aria-hidden="true">{state === "done" ? <BiCheck /> : null}</span>
                                        <span className="bv-journey-name">{s === "Pending" ? "Settlement pending" : s}</span>
                                        <span className="bv-journey-at">{reached[s] ? `${fmtDay(reached[s])}, ${fmtTime(reached[s])}` : ""}</span>
                                    </li>
                                );
                            })}
                        </ol>
                    </section>

                    <div className="bv-grid">
                        <div className="bv-main">

                            {/* 5. Vehicle and customer: one split panel, image led */}
                            <Panel id="bv-who" className="bv-who">
                                <div className="bv-who-grid">
                                    <div className="bv-veh">
                                        <div className="bv-veh-img">
                                            {car?.carImages?.[0]
                                                ? <img src={car.carImages[0]} alt={`${carName || "Vehicle"} ${car.registrationNo || ""}`} />
                                                : <div className="bv-veh-ph"><BiCar aria-hidden="true" /><span>No photo on file</span></div>}
                                        </div>
                                        <div className="bv-veh-info">
                                            <div className="bv-eyebrow">Vehicle</div>
                                            {car ? (
                                                <>
                                                    <h2 className="bv-title">{carName}</h2>
                                                    {can("vehicle.car.view") ? <Link className="bv-plate" to={`/car/details/${car.carId}`}>{car.registrationNo}</Link> : <span className="bv-plate">{car.registrationNo}</span>}
                                                    <dl className="bv-specs">
                                                        <KV label="Transmission">{car.transmissionType}</KV>
                                                        <KV label="Fuel">{[].concat(car.fuelType || []).join(", ") || "-"}</KV>
                                                        <KV label="Segment">{[car.segment, car.subSegment].filter(Boolean).map(textCapitalize).join(" · ") || "-"}</KV>
                                                        <KV label="Odometer">{car.odometer?.current != null ? `${Number(car.odometer.current).toLocaleString("en-IN")} km` : "-"}</KV>
                                                    </dl>
                                                </>
                                            ) : <p className="bv-muted">Car {booking.carId} could not be loaded.</p>}
                                        </div>
                                    </div>
                                    <div className="bv-cust">
                                        <div className="bv-eyebrow">Customer</div>
                                        {customer ? (
                                            <>
                                                <div className="bv-cust-head">
                                                    <span className="bv-avatar" aria-hidden="true">{initials(customer.name)}</span>
                                                    <div>
                                                        {can("customer.view") ? <Link className="bv-cust-name" to={`/customers/${customer.customerId}`}>{customer.name || customer.customerId}</Link> : <span className="bv-cust-name">{customer.name || customer.customerId}</span>}
                                                        <div className="bv-cust-pills">
                                                            <Pill tone={flags?.kyc ? "ok" : "warn"}>{flags?.kyc ? "KYC verified" : "KYC pending"}</Pill>
                                                            {customer.status === false && <Pill tone="bad">Deactivated</Pill>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <ul className="bv-contact">
                                                    {customer.phone && <li><BiPhone aria-hidden="true" /><span className="bv-num">{customer.phone}</span><CopyChip text={customer.phone} label="phone number" /></li>}
                                                    {customer.email && <li><BiEnvelope aria-hidden="true" /><span className="bv-ellipsis">{customer.email}</span><CopyChip text={customer.email} label="email" /></li>}
                                                </ul>
                                                <dl className="bv-specs bv-specs--tight">
                                                    <KV label="Customer ID" mono>{customer.customerId}</KV>
                                                    {typeof customer.coins === "number" && <KV label="Coins">{customer.coins.toLocaleString("en-IN")}</KV>}
                                                </dl>
                                                <div className="bv-cust-actions">
                                                    {customer.phone && <a className="bv-btn bv-btn--sm" href={`tel:${customer.phone}`}><BiPhone aria-hidden="true" /> Call</a>}
                                                    {waPhone && <a className="bv-btn bv-btn--sm" href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer"><BiLogoWhatsapp aria-hidden="true" /> WhatsApp</a>}
                                                </div>
                                            </>
                                        ) : (booking.hiddenSections || []).includes("customer")
                                            ? <p className="bv-muted">You don't have access to the customer details.</p>
                                            : <p className="bv-muted">Customer could not be loaded.</p>}
                                    </div>
                                </div>
                            </Panel>

                            {/* 6. Pickup and return as a route */}
                            {can("booking.view.handover") && (
                                <Panel id="bv-route" title="Pickup and return">
                                    <div className="bv-route">
                                        <div className="bv-route-node">
                                            <span className="bv-route-pin" aria-hidden="true"><BiMap /></span>
                                            <div className="bv-eyebrow">Pickup, {fmtDay(startDate)} {fmtTime(startDate)}</div>
                                            <div className="bv-route-mode">{preBookDetails?.deliveryDetails ? "Delivered to the customer" : "Self pickup at the hub"}</div>
                                            {preBookDetails?.deliveryDetails ? (
                                                <>
                                                    <div className="bv-route-addr">{readAddress(preBookDetails.deliveryDetails) || "Address not recorded"}</div>
                                                    <div className="bv-route-meta">{deliveryDistance != null ? `${deliveryDistance} km` : "Distance not recorded"}{deliveryFee != null ? ` · ${money(deliveryFee)}` : ""}</div>
                                                    {deliveryTask && <div className="bv-route-task"><Pill tone={deliveryTask.status === "completed" ? "ok" : "warn"}>{textCapitalize(deliveryTask.status)}</Pill> <span className="bv-mono">{deliveryTask.taskId}</span>{deliveryTask.staffId ? ` · ${deliveryTask.staffId}` : ""}</div>}
                                                </>
                                            ) : <div className="bv-route-meta">No delivery fee, customer collects the car.</div>}
                                        </div>
                                        <div className="bv-route-line" aria-hidden="true"><span>{formatDuration(startDate, endDate)}</span></div>
                                        <div className="bv-route-node">
                                            <span className="bv-route-pin" aria-hidden="true"><BiMap /></span>
                                            <div className="bv-eyebrow">Return, {fmtDay(endDate)} {fmtTime(endDate)}</div>
                                            <div className="bv-route-mode">{preBookDetails?.pickupDetails ? "Collected from the customer" : "Self return to the hub"}</div>
                                            {preBookDetails?.pickupDetails ? (
                                                <>
                                                    <div className="bv-route-addr">{readAddress(preBookDetails.pickupDetails) || "Address not recorded"}</div>
                                                    <div className="bv-route-meta">{pickupDistance != null ? `${pickupDistance} km` : "Distance not recorded"}{pickupFee != null ? ` · ${money(pickupFee)}` : ""}</div>
                                                    {pickupTask && <div className="bv-route-task"><Pill tone={pickupTask.status === "completed" ? "ok" : "warn"}>{textCapitalize(pickupTask.status)}</Pill> <span className="bv-mono">{pickupTask.taskId}</span>{pickupTask.staffId ? ` · ${pickupTask.staffId}` : ""}</div>}
                                                </>
                                            ) : <div className="bv-route-meta">No pickup fee, customer returns the car.</div>}
                                        </div>
                                    </div>
                                </Panel>
                            )}

                            {/* 7. Financial summary as a ledger */}
                            {can("booking.view.pricing") && can("booking.view.payments") && (
                                <Panel id="bv-ledger" title="Financial summary" aside={<Pill tone={payTone}>{payLabel}</Pill>}>
                                    <dl className="bv-ledger">
                                        {ledger.map((row) => (
                                            <div key={row.label} className={`bv-ledger-row${row.credit ? " is-credit" : ""}`}>
                                                <dt>{row.label}</dt>
                                                <dd className="bv-num">{row.credit ? `-${money(Math.abs(row.value))}` : money(row.value)}</dd>
                                            </div>
                                        ))}
                                        <div className="bv-ledger-row is-hold">
                                            <dt>Security deposit <span className="bv-tag">refundable</span></dt>
                                            <dd className="bv-num">{money(preBookDetails?.securityDeposit)}</dd>
                                        </div>
                                        {addOns > 0 && <div className="bv-ledger-row"><dt>Extensions and settlements <span className="bv-tag">add-ons</span></dt><dd className="bv-num">{money(addOns)}</dd></div>}
                                        <div className="bv-ledger-row is-total"><dt>Total</dt><dd className="bv-num">{money(total)}</dd></div>
                                        <div className="bv-ledger-row is-paid"><dt>Amount paid</dt><dd className="bv-num">{money(paidTotal)}</dd></div>
                                        {refundedTotal > 0 && <div className="bv-ledger-row is-credit"><dt>Refunded</dt><dd className="bv-num">-{money(refundedTotal)}</dd></div>}
                                        <div className={`bv-ledger-row ${pendingAmount > 0 ? "is-due" : "is-clear"}`}><dt>Amount pending</dt><dd className="bv-num">{money(pendingAmount)}</dd></div>
                                    </dl>
                                </Panel>
                            )}

                            {/* 7b. Exact payment breakup with the working for every amount */}
                            {can("booking.view.pricing") && can("booking.view.payments") && (
                                <Panel id="bv-breakup" title="Payment breakup and calculations">
                                    <PaymentBreakup booking={booking} payments={payments} refunds={refunds} />
                                </Panel>
                            )}

                            {/* 8. Operational detail, disclosed on demand */}
                            <div className="bv-discs">
                                {(() => {
                                    const labels = [["customer", "Customer details"], ["trip", "Trip details"], ["handover", "Pickup and return"], ["damage", "Damage"], ["pricing", "Pricing"], ["fines", "Fines and settlement"], ["payments", "Payments"]].filter(([k]) => hid(k)).map(([, l]) => l);
                                    if (!can("booking.view.log")) labels.push("Activity log");
                                    return labels.length ? <div className="bv-muted" style={{ padding: "12px 16px", borderRadius: 12, background: "var(--bv-wash, #f6f7f9)", fontSize: 13 }}>You don't have access to: {labels.join(", ")}.</div> : null;
                                })()}
                                {hasTripExecution && (
                                    <Disclosure
                                        title="Trip execution"
                                        summary={tripDetails?.endTime ? (Number(tripExcess?.hours) > 0 ? `Ended ${tripExcess.lateMinutes} min late (${tripExcess.hours} h)` : "Started and ended") : "In progress"}
                                        defaultOpen={["Started", "Ended", "Completed"].includes(stage)}
                                    >
                                        {prevExtendOrder && (
                                            <p className="bv-note bv-note--warn">An extension order for {money(prevExtendOrder.totalAmount)} was created on {formatDateTime(prevExtendOrder.createdAt)} and has not been paid.</p>
                                        )}
                                        <div className="bv-table-wrap">
                                            <table className="bv-table">
                                                <thead><tr><th scope="col"><span className="bv-sr">Field</span></th><th scope="col">At start</th><th scope="col">At return</th><th scope="col">Used</th><th scope="col">Excess</th></tr></thead>
                                                <tbody>
                                                    <tr><th scope="row">Time</th><td>{tripDetails?.startTime ? formatDateTime(tripDetails.startTime) : "-"}</td><td>{tripDetails?.endTime ? formatDateTime(tripDetails.endTime) : "-"}</td><td className="bv-num">{usedTime ?? "-"}{tripExcess?.bookedHours != null && usedTime ? <span className="bv-sub"> of {durationText(tripExcess.bookedHours)} booked</span> : null}</td><td className="bv-num">{excessCell(tripExcess?.hours, `${tripExcess?.hours} h over`)}</td></tr>
                                                    <tr><th scope="row">Odometer</th><td className="bv-num">{tripDetails?.startKm ?? tripDetails?.odometer ?? "-"}</td><td className="bv-num">{tripDetails?.endKm ?? "-"}</td><td className="bv-num">{usedKmValue != null ? `${usedKmValue} km` : "-"}{tripExcess?.kmAllowance != null && usedKmValue != null ? <span className="bv-sub"> of {tripExcess.kmAllowance} km allowed</span> : null}</td><td className="bv-num">{excessCell(tripExcess?.km, `${tripExcess?.km} km over`)}</td></tr>
                                                    <tr><th scope="row">FASTag balance</th><td className="bv-num">{tripDetails?.startFastTag ?? "-"}</td><td className="bv-num">{tripDetails?.endFastTag ?? "-"}</td><td className="bv-num">{usedFastag != null ? money(usedFastag) : "-"}</td><td className="bv-num">{excessCell(tripExcess?.fastag, `${money(tripExcess?.fastag)} short`)}</td></tr>
                                                    <tr><th scope="row">Fuel</th><td>{tripDetails?.startFuel?.map((f) => `${f.fuelType} ${f.percentage}%`).join(", ") || "-"}</td><td>{tripDetails?.endFuel?.map((f) => `${f.fuelType} ${f.percentage}%`).join(", ") || "-"}</td><td>{fuelUsedText ?? "-"}</td><td>{tripExcess?.fuel?.length ? <span className="bv-excess">{tripExcess.fuel.map((f) => `${f.fuelType} ${f.percentage}% short`).join(", ")}</span> : <span className="bv-excess-none">{tripClosed && tripExcess ? "None" : "-"}</span>}</td></tr>
                                                    <tr><th scope="row">Damage noted</th><td>{tripDetails?.startDamages?.length ? tripDetails.startDamages.map((d) => `${d.partName === "WindShield" ? "Windshield" : d.partName} (${d.damageLevel})`).join(", ") : "None"}</td><td colSpan={3}>{tripDetails?.endDamages?.length ? tripDetails.endDamages.map((d) => `${d.partName === "WindShield" ? "Windshield" : d.partName} (${d.damageLevel})`).join(", ") : "None"}</td></tr>
                                                    <tr><th scope="row">Photos</th><td>{tripDetails?.startImage?.length ? `${tripDetails.startImage.length} uploaded` : "None"}</td><td colSpan={3}>{tripDetails?.endImage?.length ? `${tripDetails.endImage.length} uploaded` : "None"}</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <Photos urls={tripDetails?.startImage} label="Start photo" />
                                        <Photos urls={tripDetails?.endImage} label="End photo" />
                                        {!!tripDetails?.legs?.length && (
                                            <div className="mb-2">
                                                <div className="bv-sub mb-1">The car was swapped during this trip. The table above shows the car now on the trip; earlier cars:</div>
                                                <ul className="bv-plain">
                                                    {tripDetails.legs.map((leg, i) => (
                                                        <li key={i}><span>{leg.carId}: {leg.startKm ?? "-"} to {leg.endKm ?? "-"} km, until {formatDateTime(leg.to)}</span><span className="bv-num">{leg.startKm != null && leg.endKm != null ? `${leg.endKm - leg.startKm} km` : ""}</span></li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {!!tripDetails?.extendedDetails?.length && (
                                            <ul className="bv-plain">
                                                {tripDetails.extendedDetails.map((ext, i) => (
                                                    <li key={i}><span>Extended {formatDateTime(ext.extendedAt)}, +{ext.extendHour} hr</span><span className="bv-mono">{ext.orderId}</span></li>
                                                ))}
                                            </ul>
                                        )}
                                    </Disclosure>
                                )}

                                {hasFineOrSettlement && (
                                    <Disclosure title="Fine and settlement" summary={settlement?.settledAt ? "Settled" : "Open"} tone={settlement?.settledAt ? undefined : "warn"} defaultOpen={["Completed", "Pending"].includes(stage)}>
                                        <dl className="bv-ledger bv-ledger--flat">
                                            {settlement?.breakdown ? (() => {
                                                const bd = settlement.breakdown;
                                                const line = (key, label, sub, item) => (item.amount > 0 || item.waived) ? (
                                                    <div key={key} className="bv-ledger-row">
                                                        <dt>{label}{sub ? <span className="bv-sub"> {sub}</span> : null}</dt>
                                                        <dd className="bv-num">{item.waived ? "Covered" : money(item.amount)}</dd>
                                                    </div>
                                                ) : null;
                                                const fuelRows = (fineDetails?.excessFuel || []).filter((f) => f.amount > 0 || bd.excessFuel?.waived);
                                                return (
                                                    <>
                                                        {line("km", "Excess km", bd.excessKm.quantity ? `${bd.excessKm.quantity} km${bd.excessKm.hourBlocks ? ` = ${bd.excessKm.hourBlocks} h x ${money(bd.excessKm.rate)}` : ""}` : "", bd.excessKm)}
                                                        {line("hr", "Late hours", bd.excessHour.quantity ? `${bd.excessHour.quantity} h x ${money(bd.excessHour.rate)}${bd.excessHour.lateMinutes ? `, ${bd.excessHour.lateMinutes} min late` : ""}` : "", bd.excessHour)}
                                                        {line("tag", "FASTag shortfall", bd.excessFastag.quantity ? `${money(bd.excessFastag.quantity)} short` : "", bd.excessFastag)}
                                                        {bd.excessFuel?.waived
                                                            ? <div className="bv-ledger-row"><dt>Fuel shortfall</dt><dd className="bv-num">Covered</dd></div>
                                                            : fuelRows.map((f, i) => <div key={`fuel${i}`} className="bv-ledger-row"><dt>Fuel, {f.fuelType}<span className="bv-sub"> {f.percentage}% short</span></dt><dd className="bv-num">{money(f.amount)}</dd></div>)}
                                                        {bd.damage?.amount > 0 && <div className="bv-ledger-row"><dt>Damage{bd.damage.repairCost ? <span className="bv-sub"> {money(bd.damage.repairCost)} repair - {money(bd.damage.coverAmount)} cover</span> : null}</dt><dd className="bv-num">{money(bd.damage.amount)}</dd></div>}
                                                        {(fineDetails?.discounts || []).map((d, i) => <div key={`disc${i}`} className="bv-ledger-row is-paid"><dt>Discount<span className="bv-sub"> {d.reason}</span></dt><dd className="bv-num">- {money(d.amount)}</dd></div>)}
                                                        {fineDetails?.fines?.map((f, i) => <div key={`fine${i}`} className="bv-ledger-row"><dt>{f.name || "Other fine"}</dt><dd className="bv-num">{money(f.amount)}</dd></div>)}
                                                        <div className="bv-ledger-row is-total"><dt>Total fine</dt><dd className="bv-num">{money(settlement.fineTotal)}</dd></div>
                                                        <div className="bv-ledger-row"><dt>Security deposit held</dt><dd className="bv-num">- {money(bd.securityDeposit)}</dd></div>
                                                        {Number(settlement.fineTotal) > Number(bd.securityDeposit)
                                                            ? <div className="bv-ledger-row is-total"><dt>Customer to pay</dt><dd className="bv-num">{money(Number(settlement.fineTotal) - Number(bd.securityDeposit))}</dd></div>
                                                            : <div className="bv-ledger-row is-paid"><dt>Deposit refunded</dt><dd className="bv-num">{money(settlement.depositRefundAmount ?? (Number(bd.securityDeposit) - Number(settlement.fineTotal)))}</dd></div>}
                                                    </>
                                                );
                                            })() : (
                                                <>
                                                    {fineDetails?.excessKm ? <div className="bv-ledger-row"><dt>Excess km</dt><dd className="bv-num">{fineDetails.excessKm}</dd></div> : null}
                                                    {fineDetails?.excessHour ? <div className="bv-ledger-row"><dt>Excess hours</dt><dd className="bv-num">{fineDetails.excessHour}</dd></div> : null}
                                                    {fineDetails?.excessFastag ? <div className="bv-ledger-row"><dt>Excess FASTag</dt><dd className="bv-num">{fineDetails.excessFastag}</dd></div> : null}
                                                    {fineDetails?.excessFuel?.map((f, i) => <div key={`fuel${i}`} className="bv-ledger-row"><dt>Fuel shortfall, {f.fuelType} {f.percentage}%</dt><dd className="bv-num">{money(f.amount)}</dd></div>)}
                                                    {fineDetails?.fines?.map((f, i) => <div key={`fine${i}`} className="bv-ledger-row"><dt>{f.name}</dt><dd className="bv-num">{money(f.amount)}</dd></div>)}
                                                    {settlement && <div className="bv-ledger-row is-total"><dt>Total fine</dt><dd className="bv-num">{money(settlement.fineTotal)}</dd></div>}
                                                    {!!settlement?.deductibleCharged && <div className="bv-ledger-row"><dt>Damage deductible</dt><dd className="bv-num">{money(settlement.deductibleCharged)}</dd></div>}
                                                    {settlement?.depositRefundAmount != null && <div className="bv-ledger-row is-paid"><dt>Deposit refunded</dt><dd className="bv-num">{money(settlement.depositRefundAmount)}</dd></div>}
                                                </>
                                            )}
                                        </dl>
                                        {settlement && <p className="bv-muted">{settlement.settledAt ? `Settled ${formatDateTime(settlement.settledAt)}` : "Awaiting settlement payment"}</p>}
                                    </Disclosure>
                                )}

                                {damage && (
                                    <Disclosure title="Damage" summary={damage.damageStatus} tone="bad" defaultOpen>
                                        <dl className="bv-specs">
                                            <KV label="Status">{damage.damageStatus}</KV>
                                            <KV label="Service">{damage.service}</KV>
                                            <KV label="Customer charged?">{damage.deductible === false ? "No" : "Yes"}</KV>
                                            <KV label="Repair cost">{damage.repairCost != null ? money(damage.repairCost) : "-"}</KV>
                                            <KV label="Protection cover">{money(coverAmount)}</KV>
                                            <KV label="Damage fine">{damage.deductibleCharged != null ? money(damage.deductibleCharged) : damage.deductible === false ? "None" : `${money(Math.max(0, Number(damage.repairCost || 0) - Number(coverAmount || 0)))} (charged with the fine charges)`}</KV>
                                            <KV label="Settled">{damage.settledAt ? formatDateTime(damage.settledAt) : "Not settled"}</KV>
                                        </dl>
                                        <Photos urls={damage.damageImages} label="Damage photo" />
                                    </Disclosure>
                                )}

                                {hasCancellation && (
                                    <Disclosure title="Cancellation and refund" summary={cancelDetails?.refund ? `${money(cancelDetails.refund.totalRefund)} refunded` : undefined} tone="bad" defaultOpen={stage === "Cancelled"}>
                                        <dl className="bv-specs">
                                            <KV label="Cancelled at">{cancelDetails?.at ? formatDateTime(cancelDetails.at) : "-"}</KV>
                                            <KV label="Before pickup">{cancelDetails?.refund?.hoursBeforeStart != null ? `${cancelDetails.refund.hoursBeforeStart} hrs` : "-"}</KV>
                                            <KV label="Refund ID" mono>{booking.refundId || "-"}</KV>
                                            <KV label="Reason">{cancelDetails?.reason || "-"}</KV>
                                        </dl>
                                        {!!cancelDetails?.refund?.lines?.length && (
                                            <div className="bv-table-wrap">
                                                <table className="bv-table">
                                                    <thead><tr><th scope="col">Component</th><th scope="col" className="is-r">Paid</th><th scope="col" className="is-r">Refund %</th><th scope="col" className="is-r">Refunded</th></tr></thead>
                                                    <tbody>
                                                        {cancelDetails.refund.lines.map((line, i) => (
                                                            <tr key={i}><th scope="row">{REFUND_LINE_LABEL[line.component] || textCapitalize(line.component)}</th><td className="bv-num is-r">{money(line.paid)}</td><td className="bv-num is-r">{line.percentage ?? 0}%</td><td className="bv-num is-r">{money(line.refund)}</td></tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        {cancelDetails?.refund && (
                                            <dl className="bv-ledger bv-ledger--flat">
                                                <div className="bv-ledger-row"><dt>Total paid</dt><dd className="bv-num">{money(cancelDetails.refund.totalPaid)}</dd></div>
                                                <div className="bv-ledger-row is-paid"><dt>Total refunded</dt><dd className="bv-num">{money(cancelDetails.refund.totalRefund)}</dd></div>
                                                <div className="bv-ledger-row is-total"><dt>Forfeited</dt><dd className="bv-num">{money(cancelDetails.refund.forfeited)}</dd></div>
                                            </dl>
                                        )}
                                    </Disclosure>
                                )}

                                <Disclosure title="Booking references" summary="IDs, package, protection">
                                    <dl className="bv-specs">
                                        <KV label="Booking ID" mono>{booking.bookingId}</KV>
                                        <KV label="Order ID" mono>{preBookDetails?.orderId}</KV>
                                        <KV label="Customer ID" mono>{booking.customerId}</KV>
                                        <KV label="Car ID" mono>{booking.carId}</KV>
                                        <KV label="Package">{preBookDetails?.package?.package ? `${textCapitalize(preBookDetails.package.package)}, ${preBookDetails.package.perHourKm ?? "-"} km/hr` : "-"}</KV>
                                        <KV label="Protection">{preBookDetails?.tripProtection?.name}</KV>
                                        <KV label="Delivery task" mono>{deliveryTask?.taskId}</KV>
                                        <KV label="Pickup task" mono>{pickupTask?.taskId}</KV>
                                    </dl>
                                </Disclosure>
                            </div>
                        </div>

                        <aside className="bv-side" aria-label="Payment and activity">

                            {/* 9. Payment */}
                            {can("booking.view.payments") && (
                                <Panel id="bv-pay" title="Payment" aside={<Pill tone={payTone}>{payLabel}</Pill>} className="bv-pay">
                                    <div className="bv-pay-card">
                                        <div className="bv-pay-figure">
                                            <span className="bv-pay-amount bv-num">{money(stage === "Initiated" ? bookingPaid : paidTotal)}</span>
                                            <span className="bv-pay-of">of <span className="bv-num">{money(total)}</span></span>
                                        </div>
                                        <div className="bv-meter" role="img" aria-label={`${payPct}% of the total is paid`}><span style={{ width: `${payPct}%` }} /></div>
                                        <div className="bv-pay-foot">
                                            <span>{pendingAmount > 0 ? <span className="bv-text-warn">{money(pendingAmount)} pending</span> : "Nothing pending"}</span>
                                            <span>Razorpay</span>
                                        </div>
                                    </div>

                                    {dueItems.length > 0 && (
                                        <div className="bv-due">
                                            <div className="bv-eyebrow bv-eyebrow--gap">Awaiting payment</div>
                                            {dueItems.map((d) => {
                                                const link = linkFor(d.kind, d.orderId);
                                                const expireKey = link ? `expire:${link.linkId}` : null;
                                                const cancelKey = `cancel:${d.orderId}`;
                                                return (
                                                    <div className="bv-link-box" key={d.key}>
                                                        <div className="bv-due-head"><strong>{d.title}</strong><span className="bv-num">{money(d.amount)}</span></div>
                                                        <div className="bv-link-meta">{d.createdAt ? `Ordered ${formatDateTime(d.createdAt)}, not paid yet` : "Not paid yet"}</div>
                                                        {link ? (
                                                            <>
                                                                <div className="bv-link-url">
                                                                    <span className="bv-mono bv-ellipsis" title={link.shortUrl}>{link.shortUrl}</span>
                                                                    <CopyChip text={link.shortUrl} label="payment link" />
                                                                    <a className="bv-icon-btn" href={link.shortUrl} target="_blank" rel="noopener noreferrer" aria-label="Open payment link" title="Open"><BiLinkExternal aria-hidden="true" /></a>
                                                                </div>
                                                                <div className="bv-link-actions">
                                                                    <button type="button" className="bv-btn bv-btn--sm" disabled={linkBusy} onClick={() => resendPaymentLink(link.linkId, "email")}>Resend email</button>
                                                                    <button type="button" className="bv-btn bv-btn--sm" disabled={linkBusy} onClick={() => resendPaymentLink(link.linkId, "sms")}>Resend SMS</button>
                                                                    {waPhone && (
                                                                        <a className="bv-btn bv-btn--sm" target="_blank" rel="noopener noreferrer"
                                                                            href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Your ${d.title.toLowerCase()} for booking ${booking.bookingId} (${money(d.amount)}) is waiting for payment: ${link.shortUrl}`)}`}>
                                                                            <BiLogoWhatsapp aria-hidden="true" /> Share
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="bv-link-actions">
                                                                <button type="button" className="bv-btn bv-btn--sm bv-btn--primary" disabled={linkBusy} onClick={() => createPaymentLink(d.orderId)}>Create payment link</button>
                                                            </div>
                                                        )}
                                                        {confirmKey === cancelKey || (expireKey && confirmKey === expireKey) ? (
                                                            <div className="bv-confirm" role="alertdialog" aria-label={confirmKey === cancelKey ? "Confirm cancelling the extension" : "Confirm expiring the payment link"}>
                                                                <span>{confirmKey === cancelKey ? "Cancel this extension? Its payment link stops working and the trip end time stays as it is." : "Expire this link? The customer will no longer be able to pay with it."}</span>
                                                                <div>
                                                                    <button type="button" className="bv-btn bv-btn--sm bv-btn--danger" disabled={linkBusy} onClick={() => (confirmKey === cancelKey ? cancelExtension(d.orderId) : expirePaymentLink(link.linkId))}>{confirmKey === cancelKey ? "Cancel extension" : "Expire link"}</button>
                                                                    <button type="button" className="bv-btn bv-btn--sm" onClick={() => setConfirmKey(null)}>Keep it</button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="bv-due-foot">
                                                                {d.kind === "Extension" && <button type="button" className="bv-link bv-link--danger" onClick={() => setConfirmKey(cancelKey)}>Cancel extension</button>}
                                                                {link && <button type="button" className="bv-link bv-link--danger" onClick={() => setConfirmKey(expireKey)}>Expire link</button>}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="bv-eyebrow bv-eyebrow--gap">History</div>
                                    {payments.length === 0 && refunds.length === 0 ? (
                                        <p className="bv-empty">No payment activity yet. Orders and refunds will appear here as they happen.</p>
                                    ) : (
                                        <ul className="bv-pay-list">
                                            {payments.filter((p) => !p.paidOrderId || p.duplicate?.reason).map((p) => (
                                                <li key={p.orderId}>
                                                    <div>
                                                        <div className="bv-pay-for">{p.paymentFor}{p.channel ? ` · ${p.channel === "link" ? "Payment link" : "Checkout (SDK)"}` : ""}</div>
                                                        {p.sourceOrderId && <div className="bv-pay-when">Paid in place of order <span className="bv-mono">{p.sourceOrderId}</span></div>}
                                                        {p.duplicate?.reason && (
                                                            <div className="bv-pay-when bv-text-warn">
                                                                {p.duplicate.reason === "duplicate" ? `Duplicate - already paid by ${p.duplicate.of || "another payment"}` : "Late payment - not applied"}
                                                                {" · "}{({ pending: "refund awaiting approval", refunded: "refunded", kept: "refund rejected, kept", failed: "refund failed" })[p.duplicate.status] || "under review"}
                                                                {p.duplicate.status === "pending" && <> · <Link to="/payments?tab=approvals">Review</Link></>}
                                                            </div>
                                                        )}
                                                        <div className="bv-pay-ref"><span className="bv-mono" title="Order ID">{p.orderId}</span><CopyChip text={p.orderId} label="order ID" /></div>
                                                        {p.paymentId && <div className="bv-pay-ref"><span className="bv-mono" title="Transaction ID">{p.paymentId}</span><CopyChip text={p.paymentId} label="transaction ID" /></div>}
                                                        <div className="bv-pay-when">{formatDateTime(p.createdAt)}</div>
                                                    </div>
                                                    <div className="bv-pay-right">
                                                        <span className="bv-num">{money(p.totalAmount)}</span>
                                                        <Pill tone={PAID_STATES.includes(p.status) ? "ok" : p.status === "failed" ? "bad" : "neutral"}>{textCapitalize(String(p.status || "").replace(/_/g, " "))}</Pill>
                                                        {p.refundedAmount ? <span className="bv-pay-refunded">-{money(p.refundedAmount)} refunded</span> : null}
                                                    </div>
                                                </li>
                                            ))}
                                            {refunds.map((r, i) => (
                                                <li key={`r${r.refundId || i}`}>
                                                    <div><div className="bv-pay-for">Refund</div><div className="bv-pay-ref"><span className="bv-mono">{r.refundId || r.paymentId}</span></div></div>
                                                    <div className="bv-pay-right"><span className="bv-num">-{money(r.amount)}</span><Pill tone="info">{textCapitalize(r.status || "issued")}</Pill></div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </Panel>
                            )}

                            {/* 10. Activity */}
                            {can("booking.view.log") && (
                                <Panel
                                    id="bv-activity"
                                    title="Activity"
                                    aside={(
                                        <div className="bv-chips" role="group" aria-label="Filter activity">
                                            {[["all", "All"], ["status", "Status"], ["payment", "Payments"], ["ops", "Ops"]].map(([key, label]) => (
                                                <button key={key} type="button" className="bv-chip" aria-pressed={activityFilter === key} onClick={() => { setActivityFilter(key); setActivityAll(false); }}>{label}</button>
                                            ))}
                                        </div>
                                    )}
                                >
                                    {filteredActivity.length === 0 ? (
                                        <p className="bv-empty">Nothing recorded for this filter.</p>
                                    ) : (
                                        <ol className="bv-feed">
                                            {shownActivity.map((item) => (
                                                <li key={item.id} className={`is-${item.kind}${item.priority ? " is-priority" : ""}`}>
                                                    <span className={`bv-feed-dot bv-feed-dot--${item.tone || "neutral"}`} aria-hidden="true" />
                                                    <div className="bv-feed-body">
                                                        <div className="bv-feed-title">{item.title}</div>
                                                        {item.sub ? <div className="bv-feed-sub">{item.sub}</div> : null}
                                                        <div className="bv-feed-meta"><BiTimeFive aria-hidden="true" />{formatDateTime(item.at)}{item.who ? ` · ${item.who}` : ""}</div>
                                                        {item.changes && (
                                                            <details className="bv-feed-changes"><summary>View changes</summary><pre>{JSON.stringify(item.changes, null, 2)}</pre></details>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                    {filteredActivity.length > ACTIVITY_PREVIEW && (
                                        <button type="button" className="bv-link bv-feed-more" aria-expanded={activityAll} onClick={() => setActivityAll((v) => !v)}>
                                            {activityAll ? "Show fewer" : `Show all ${filteredActivity.length} events`}
                                        </button>
                                    )}
                                </Panel>
                            )}
                        </aside>
                    </div>
                </div>
            </div>

            <div className="bvx">
                {showUploadImages && (
                    <UploadStartImagesModal
                        bookingId={booking.bookingId}
                        initialImages={tripDetails?.startImage}
                        initialDamages={tripDetails?.startDamages}
                        onClose={() => setShowUploadImages(false)}
                        onUploaded={() => {
                            setShowUploadImages(false);
                            load();
                        }}
                    />
                )}

                {showStartTrip && (
                    <StartTripModal
                        bookingId={booking.bookingId}
                        fuelTypes={car?.fuelType}
                        onClose={() => setShowStartTrip(false)}
                        onStarted={() => load({ quiet: true })}
                        currentKm={booking?.car?.odometer?.current}
                    />
                )}
                {showExtend && (
                    <ExtendTripModal
                        bookingId={booking.bookingId}
                        customerName={customer?.name}
                        customerPhone={customer?.phone}
                        prevExtendOrder={prevExtendOrder}
                        endDate={preBookDetails?.endDate}
                        extendedHours={sum(tripDetails?.extendedDetails || [], (e) => e.extendHour)}
                        extension={booking.extension}
                        carLabel={carName ? `${carName}${car?.registrationNo ? `, ${car.registrationNo}` : ""}` : undefined}
                        onClose={() => setShowExtend(false)}
                        onExtended={() => load({ quiet: true })}
                    />
                )}

                {showCloseTrip && (
                    <CloseTripModal
                        bookingId={booking.bookingId}
                        savedImages={tripDetails?.endImage}
                        minImages={endPhotoLimits.min}
                        maxImages={endPhotoLimits.max}
                        ended={stage === "Ended"}
                        dueAt={preBookDetails?.endDate}
                        graceMinutes={bookingPolicy.excessHourGraceMinutes ?? 30}
                        onClose={() => setShowCloseTrip(false)}
                        onClosed={() => load({ quiet: true })}
                    />
                )}

                {showEndTime && (
                <EditEndTimeModal
                    bookingId={booking.bookingId}
                    endTime={tripDetails?.endTime}
                    startTime={tripDetails?.startTime}
                    dueAt={preBookDetails?.endDate}
                    onClose={() => setShowEndTime(false)}
                    onSaved={() => load({ quiet: true })}
                />
            )}

            {showCompleteTrip && (
                    <CompleteTripModal
                        bookingId={booking.bookingId}
                        fuelTypes={car?.fuelType}
                        startKm={tripDetails?.startKm ?? tripDetails?.odometer}
                        onClose={() => setShowCompleteTrip(false)}
                        onDone={() => load({ quiet: true })}
                        onFines={can("booking.inspect") ? () => { setShowCompleteTrip(false); setShowInspect(true); } : undefined}
                    />
                )}

                {showDamage && (
                    <DamageModal
                        bookingId={booking.bookingId}
                        canWaive={can("booking.damage.waive")}
                        existing={damage}
                        coverAmount={coverAmount}
                        onClose={() => setShowDamage(false)}
                        onLogged={() => load({ quiet: true })}
                    />
                )}

                {showInspect && (
                    <InspectModal
                        bookingId={booking.bookingId}
                        canDiscount={can("booking.inspect.override")}
                        canSettle={can("booking.settle")}
                        customerName={customer?.name}
                        customerPhone={customer?.phone}
                        prevSettlementOrder={stage === "Pending" ? prevSettlementOrder : null}
                        settlementLink={liveLinks.find((l) => l.paymentFor === "Settlement")?.shortUrl}
                        excess={booking?.tripDetails?.excess}
                        onClose={() => setShowInspect(false)}
                        onInspected={() => load({ quiet: true })}
                    />
                )}

                {showRefund && (
                    <RefundModal
                        payments={refundableRows}
                        onClose={() => setShowRefund(false)}
                        onDone={() => load({ quiet: true })}
                    />
                )}

                {showCancel && (
                    <CancelModal
                        bookingId={booking.bookingId}
                        onClose={() => setShowCancel(false)}
                        onCancelled={() => load({ quiet: true })}
                    />
                )}

                {showSwap && (
                    <SwapCarModal
                        bookingId={booking.bookingId}
                        currentCarId={booking.carId}
                        endDate={preBookDetails?.endDate}
                        perHourKm={preBookDetails?.package?.perHourKm}
                        fuelTypes={car?.fuelType}
                        startKm={tripDetails?.startKm ?? tripDetails?.odometer}
                        endLimits={endPhotoLimits}
                        startLimits={startPhotoLimits}
                        onClose={() => setShowSwap(false)}
                        onSwapped={() => load({ quiet: true })}
                    />
                )}

                {showCarChange && (
                    <CarChangeModal
                        bookingId={booking.bookingId}
                        currentCarId={booking.carId}
                        startDate={preBookDetails?.startDate}
                        endDate={preBookDetails?.endDate}
                        perHourKm={preBookDetails?.package?.perHourKm}
                        onClose={() => setShowCarChange(false)}
                        onChanged={() => load({ quiet: true })}
                    />
                )}
            </div>
        </>
    );
};

export default BookingView;
